export type CountryPreferenceSource = "saved" | "ip" | "locale" | "fallback";

export function normalizeCountryCode(
  value: string | null | undefined,
): string | null {
  const normalized = value?.trim().toUpperCase();
  if (!normalized || !/^[A-Z]{2}$/.test(normalized)) return null;
  return normalized === "UK" ? "GB" : normalized;
}

export function resolveCountryPreference({
  savedCountry,
  ipCountry,
  localeCountry,
  supportedCountryCodes,
  fallbackCountry,
}: {
  savedCountry?: string | null;
  ipCountry?: string | null;
  localeCountry?: string | null;
  supportedCountryCodes: readonly string[];
  fallbackCountry: string;
}): { countryCode: string; source: CountryPreferenceSource } {
  const supported = new Set(
    supportedCountryCodes
      .map((value) => normalizeCountryCode(value))
      .filter((value): value is string => value !== null),
  );
  const candidates: {
    value: string | null | undefined;
    source: Exclude<CountryPreferenceSource, "fallback">;
  }[] = [
    { value: savedCountry, source: "saved" },
    { value: ipCountry, source: "ip" },
    { value: localeCountry, source: "locale" },
  ];

  for (const candidate of candidates) {
    const countryCode = normalizeCountryCode(candidate.value);
    if (countryCode && supported.has(countryCode)) {
      return { countryCode, source: candidate.source };
    }
  }

  return {
    countryCode: normalizeCountryCode(fallbackCountry) ?? "US",
    source: "fallback",
  };
}
